import React, {useEffect, useMemo, useState} from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import moment, {Moment} from 'moment';
import {connect} from 'react-redux';

import KhulaLogo from 'assets/images/logo.png';
import {
  IDepotStatistics,
  IFarmInputInvoice,
  IFarmInputProgram,
  IFarmInputQuote,
  IFdApplicationType,
  IRouteScreenProps,
  ISupplierAnalytics,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiGetAllFdApplications,
  apiGetDepotOrders,
  apiGetDepotQuotes,
  apiGetDepotStatistics,
  apiGetInputProductNames,
  apiGetInputPrograms,
  apiGetSupportCustomers,
  apiGetSupplierAnalytics,
} from 'constants/Api';
import {
  lshFilterFdApplicationsForLinkedCustomers,
  lshFilterInputProgramsForLinkedCustomers,
} from 'services/SupplierCustomerScope';
import {
  AnalyticsFunnelChart,
  toSalesCountFunnelData,
  toSalesValueFunnelData,
} from 'screens/Dashboard/Analytics/AnalyticsFunnelChart';
import {
  AnalyticsPieChart,
  toOrderTypeData,
  toQuoteTypeData,
} from 'screens/Dashboard/Analytics/AnalyticsPieChart';
import {
  AnalyticsEngagementList,
  toTopProductsData,
} from 'screens/Dashboard/Analytics/AnalyticsEngagementList';

import {
  AnalyticsBreakdownList,
  AnalyticsCard,
  AnalyticsMetric,
  AnalyticsSection,
  AnalyticsTopProductsList,
} from './AnalyticsCards';
import {AnalyticsFilters} from './AnalyticsFilters';
import {AnalyticsReportDrawer} from './AnalyticsReportDrawer';
import {
  lshBuildFundingCategoryRows,
  lshBuildFundingStatusRows,
  lshBuildProgramRows,
  lshBuildTopOrderProducts,
  lshBuildTopQuoteProducts,
  lshFormatCurrency,
  lshFormatInteger,
  lshFundingAskValue,
  lshIsDateWithinRange,
  lshShouldShowSupplierEngagementAnalytics,
} from './analyticsData';
import {
  lshBuildAnalyticsReportFileName,
  lshSaveAnalyticsReportPdf,
} from './analyticsReportPdf';
import {lshBuildAnalyticsReportHtml} from './analyticsReportHtml';
import {
  EAnalyticsDateRange,
  DEFAULT_REPORT_SECTIONS,
  ReportSectionId,
  ReportSectionOption,
} from './analyticsTypes';

type AnalyticsProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
};

type QuoteWithInsertedOn = IFarmInputQuote & {
  insertedOn?: string;
};

function Analytics(props: AnalyticsProps) {
  const showSupplierEngagementAnalytics =
    lshShouldShowSupplierEngagementAnalytics(props.user);
  const [dateRangeId, setDateRangeId] = useState<EAnalyticsDateRange>(
    EAnalyticsDateRange.THIS_MONTH,
  );
  const [startDate, setStartDate] = useState<Moment | null>();
  const [endDate, setEndDate] = useState<Moment | null>();
  const [stats, setStats] = useState<IDepotStatistics>();
  const [analytics, setAnalytics] = useState<ISupplierAnalytics>();
  const [applications, setApplications] = useState<IFdApplicationType[]>([]);
  const [programs, setPrograms] = useState<IFarmInputProgram[]>([]);
  const [orders, setOrders] = useState<IFarmInputInvoice[]>([]);
  const [quotes, setQuotes] = useState<IFarmInputQuote[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsFailed, setDetailsFailed] = useState(false);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [selectedReportSections, setSelectedReportSections] = useState<
    ReportSectionId[]
  >(DEFAULT_REPORT_SECTIONS);

  useEffect(() => {
    if (!startDate || !endDate) return;
    apiGetDepotStatistics(startDate.toDate(), endDate.toDate())
      .then(setStats)
      .catch((err) => {
        console.error('Failed to fetch depot statistics', err);
        alert(
          'Failed to fetch analytics at the moment. Please try again later',
        );
      });
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let alive = true;
    let failed = false;

    const markFailed = (label: string) => (error: unknown) => {
      console.error(`Failed to fetch ${label}`, error);
      failed = true;
      return [];
    };

    setDetailsLoading(true);
    setDetailsFailed(false);

    Promise.all([
      apiGetAllFdApplications().catch(markFailed('funding applications')),
      apiGetInputPrograms(500).catch(markFailed('input programs')),
      apiGetDepotOrders(500).catch(markFailed('orders')),
      apiGetDepotQuotes(500).catch(markFailed('quotes')),
      apiGetSupportCustomers().catch((error) => {
        console.error('Failed to fetch support customers', error);
        failed = true;
        return undefined;
      }),
    ])
      .then(
        ([
          applicationResponse,
          programResponse,
          orderResponse,
          quoteResponse,
          customers,
        ]) => {
          if (!alive) return;

          const scopedApplications = customers
            ? lshFilterFdApplicationsForLinkedCustomers(
                applicationResponse as IFdApplicationType[],
                customers,
              )
            : (applicationResponse as IFdApplicationType[]);
          const scopedPrograms = customers
            ? lshFilterInputProgramsForLinkedCustomers(
                programResponse as IFarmInputProgram[],
                customers,
              )
            : (programResponse as IFarmInputProgram[]);

          setApplications(
            scopedApplications.filter((application) =>
              lshIsDateWithinRange(application.createdAt, startDate, endDate),
            ),
          );
          setPrograms(
            scopedPrograms.filter((program) =>
              lshIsDateWithinRange(program.createdDate, startDate, endDate),
            ),
          );
          setOrders(
            (orderResponse as IFarmInputInvoice[]).filter((order) =>
              lshIsDateWithinRange(order.insertedDate, startDate, endDate),
            ),
          );
          setQuotes(
            (quoteResponse as IFarmInputQuote[]).filter((quote) => {
              const quoteDate =
                (quote as QuoteWithInsertedOn).insertedOn || quote.insertedDate;
              return lshIsDateWithinRange(quoteDate, startDate, endDate);
            }),
          );
          setDetailsFailed(failed);
        },
      )
      .finally(() => {
        if (alive) setDetailsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    const brandId = props.user.brand?.id;
    if (!showSupplierEngagementAnalytics) {
      setAnalytics(undefined);
      return;
    }
    if (!startDate || !endDate || !brandId) return;

    apiGetSupplierAnalytics(
      startDate.toDate(),
      endDate.toDate(),
      parseInt(brandId),
    )
      .then(async (analytics) => {
        const mostPopularProductIds = analytics.mostPopular.map(
          (item) => item.productId,
        );
        const mostPopularProducts = await apiGetInputProductNames(
          mostPopularProductIds,
        );
        analytics.mostPopular.forEach(
          (item) =>
            (item.product = mostPopularProducts.find(
              (p) => p.id == item.productId,
            )),
        );
        return analytics;
      })
      .then(setAnalytics)
      .catch((err) => {
        console.error('Failed to fetch supplier analytics', err);
        alert(
          'Failed to fetch analytics at the moment. Please try again later',
        );
      });
  }, [
    startDate,
    endDate,
    props.user.brand?.id,
    showSupplierEngagementAnalytics,
  ]);

  useEffect(() => {
    switch (dateRangeId) {
      case EAnalyticsDateRange.TODAY: {
        setStartDate(moment().startOf('day'));
        setEndDate(moment().endOf('day'));
        break;
      }
      case EAnalyticsDateRange.THIS_WEEK: {
        setStartDate(moment().startOf('week'));
        setEndDate(moment().endOf('week'));
        break;
      }
      case EAnalyticsDateRange.THIS_MONTH: {
        setStartDate(moment().startOf('month'));
        setEndDate(moment().endOf('month'));
        break;
      }
      case EAnalyticsDateRange.THIS_YEAR: {
        setStartDate(moment().startOf('year'));
        setEndDate(moment().endOf('year'));
        break;
      }
      case EAnalyticsDateRange.LAST_7_DAYS: {
        setStartDate(moment().subtract(7, 'days').startOf('day'));
        setEndDate(moment().endOf('day'));
        break;
      }
      case EAnalyticsDateRange.LAST_30_DAYS: {
        setStartDate(moment().subtract(30, 'days').startOf('day'));
        setEndDate(moment().endOf('day'));
        break;
      }
      case EAnalyticsDateRange.ALL_TIME: {
        setStartDate(moment('2017-01-01T00:00:00'));
        setEndDate(moment().endOf('day'));
        break;
      }
    }
  }, [dateRangeId]);

  const handleSetStartDate = (date: Moment | null) => {
    setDateRangeId(EAnalyticsDateRange.CUSTOM);
    setStartDate(date);
  };
  const handleSetEndDate = (date: Moment | null) => {
    setDateRangeId(EAnalyticsDateRange.CUSTOM);
    setEndDate(date);
  };

  const totalFundingAsk = useMemo(
    () =>
      applications.reduce(
        (total, application) => total + lshFundingAskValue(application),
        0,
      ),
    [applications],
  );
  const averageApplicationCompletion = useMemo(
    () =>
      applications.length
        ? Math.round(
            applications.reduce(
              (total, application) =>
                total +
                Number(
                  application.completedSectionsPercentage ??
                    application.progress ??
                    0,
                ),
              0,
            ) / applications.length,
          )
        : 0,
    [applications],
  );
  const totalProgramHectares = useMemo(
    () =>
      programs.reduce(
        (total, program) => total + Number(program.hectares || 0),
        0,
      ),
    [programs],
  );
  const fundingStatusRows = useMemo(
    () => lshBuildFundingStatusRows(applications),
    [applications],
  );
  const fundingCategoryRows = useMemo(
    () => lshBuildFundingCategoryRows(applications),
    [applications],
  );
  const programStatusRows = useMemo(
    () => lshBuildProgramRows(programs, (program) => program.status),
    [programs],
  );
  const programTypeRows = useMemo(
    () => lshBuildProgramRows(programs, (program) => program.type),
    [programs],
  );
  const topQuoteProducts = useMemo(
    () => lshBuildTopQuoteProducts(quotes),
    [quotes],
  );
  const topOrderProducts = useMemo(
    () => lshBuildTopOrderProducts(orders),
    [orders],
  );
  const salesConversion = stats?.quotesCount
    ? Math.round((Number(stats.invoicesCount || 0) / stats.quotesCount) * 100)
    : 0;
  const paidOrderRate = stats?.invoicesCount
    ? Math.round(
        (Number(stats.paidInvoicesCount || 0) / stats.invoicesCount) * 100,
      )
    : 0;
  const reportSectionOptions = useMemo<ReportSectionOption[]>(() => {
    const options: ReportSectionOption[] = [
      {
        id: 'summary',
        label: 'Executive summary',
        description: 'Headline quote, order, funding, and program metrics.',
      },
      {
        id: 'orderQuotes',
        label: 'Order and quote analytics',
        description: 'Sales conversion by count and value, plus statuses.',
      },
      {
        id: 'funding',
        label: 'Funding analytics',
        description: 'Funding ask, application statuses, and cost categories.',
      },
      {
        id: 'programs',
        label: 'Program analytics',
        description: 'Program count, hectares, status, and program type.',
      },
      {
        id: 'productActivity',
        label: 'Product activity',
        description: 'Top products from quotes and orders.',
      },
    ];

    if (showSupplierEngagementAnalytics && analytics) {
      options.push({
        id: 'supplierEngagement',
        label: 'Supplier engagement',
        description: 'Most viewed products for supplier companies.',
      });
    }

    return options;
  }, [analytics, showSupplierEngagementAnalytics]);
  const reportRangeLabel =
    startDate && endDate
      ? `${startDate.format('D MMM YYYY')} - ${endDate.format('D MMM YYYY')}`
      : 'Date range not selected';
  const companyName =
    props.user.company?.tradingName ||
    props.user.company?.name ||
    props.user.brand?.name ||
    props.user.mainSupplier?.name ||
    'Khula supplier';
  const companyLogo =
    props.user.company?.image ||
    props.user.brand?.image ||
    props.user.mainSupplier?.image;
  const supplierEngagementRows = useMemo(
    () =>
      analytics
        ? toTopProductsData(analytics).items.map((item) => ({
            name: String(item.name || 'Product'),
            numberOfViews: Number(item.numberOfViews || 0),
          }))
        : [],
    [analytics],
  );

  const toggleReportSection = (sectionId: ReportSectionId) => {
    setSelectedReportSections((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId],
    );
  };

  const handleGenerateAnalyticsReport = async () => {
    if (!stats || !startDate || !endDate) return;
    if (reportGenerating) return;

    const availableSectionIds = reportSectionOptions.map((option) => option.id);
    const reportSections = selectedReportSections.filter((sectionId) =>
      availableSectionIds.includes(sectionId),
    );

    if (!reportSections.length) {
      alert('Select at least one section to include in the report.');
      return;
    }

    const reportHtml = lshBuildAnalyticsReportHtml({
      companyName,
      companyLogo,
      generatedAt: moment(),
      khulaLogo: KhulaLogo,
      rangeLabel: reportRangeLabel,
      selectedSections: reportSections,
      stats,
      applications,
      programs,
      totalFundingAsk,
      averageApplicationCompletion,
      totalProgramHectares,
      salesConversion,
      paidOrderRate,
      fundingStatusRows,
      fundingCategoryRows,
      programStatusRows,
      programTypeRows,
      topQuoteProducts,
      topOrderProducts,
      supplierEngagementRows,
    });

    setReportGenerating(true);

    try {
      await lshSaveAnalyticsReportPdf(
        reportHtml,
        lshBuildAnalyticsReportFileName(companyName),
      );
      setReportDrawerOpen(false);
    } catch {
      alert('We could not save the analytics report. Please try again.');
    } finally {
      setReportGenerating(false);
    }
  };

  return (
    <div className="analytics-container page-container roboto">
      <div className="header-container">
        <Typography variant="h4" className="page-title">
          Analytics
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          {showSupplierEngagementAnalytics
            ? 'Product and sales analytics for your depot'
            : 'Order, quote, funding, and program analytics'}
        </Typography>
        <div className="analytics-controls-bar">
          <div className="analytics-controls-row">
            <AnalyticsFilters
              dateRangeId={dateRangeId}
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={setDateRangeId}
              onStartDateChange={handleSetStartDate}
              onEndDateChange={handleSetEndDate}
            />
            <div className="analytics-report-toolbar">
              <Button
                variant="outlined"
                className="analytics-export-button"
                startIcon={<GetAppIcon />}
                onClick={() => setReportDrawerOpen(true)}
              >
                Export PDF
              </Button>
            </div>
          </div>
          <Box className="analytics-header-divider">
            <Divider />
          </Box>
        </div>
      </div>

      {!stats || (showSupplierEngagementAnalytics && !analytics) ? (
        <Box pt={2} display="flex" justifyContent="center">
          <CircularProgress color="primary" size={24} thickness={6} />
        </Box>
      ) : (
        <Box className="body-container">
          <Paper variant="outlined" className="analytics-panel">
            {detailsFailed && (
              <Typography className="analytics-error">
                Some detailed analytics could not be loaded.
              </Typography>
            )}
            <div className="analytics-kpi-grid">
              <AnalyticsMetric
                label="Quotes"
                value={lshFormatInteger(stats.quotesCount)}
                detail={lshFormatCurrency(stats.quotesTotal)}
              />
              <AnalyticsMetric
                label="Orders"
                value={lshFormatInteger(stats.invoicesCount)}
                detail={lshFormatCurrency(stats.invoicesTotal)}
              />
              <AnalyticsMetric
                label="Paid orders"
                value={lshFormatInteger(stats.paidInvoicesCount)}
                detail={`${paidOrderRate}% paid`}
              />
              <AnalyticsMetric
                label="Conversion"
                value={`${salesConversion}%`}
                detail="Quotes to orders"
              />
              <AnalyticsMetric
                label="Funding ask"
                value={lshFormatCurrency(totalFundingAsk)}
                detail={`${lshFormatInteger(applications.length)} applications`}
              />
              <AnalyticsMetric
                label="Programs"
                value={lshFormatInteger(programs.length)}
                detail={`${lshFormatInteger(totalProgramHectares)} ha`}
              />
            </div>

            <AnalyticsSection title="Order and quote analytics">
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <AnalyticsCard title="Sales Conversion Funnel (#)">
                    <AnalyticsFunnelChart
                      data={toSalesCountFunnelData(stats)}
                    />
                  </AnalyticsCard>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsCard title="Sales Conversion Funnel (R)">
                    <AnalyticsFunnelChart
                      data={toSalesValueFunnelData(stats)}
                    />
                  </AnalyticsCard>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <AnalyticsCard title="Quotes">
                    <AnalyticsPieChart data={toQuoteTypeData(stats)} />
                  </AnalyticsCard>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <AnalyticsCard title="Orders">
                    <AnalyticsPieChart data={toOrderTypeData(stats)} />
                  </AnalyticsCard>
                </Grid>
                {showSupplierEngagementAnalytics && analytics && (
                  <Grid item xs={12} lg={4}>
                    <AnalyticsCard title="Top Products">
                      <AnalyticsEngagementList
                        data={toTopProductsData(analytics)}
                      />
                    </AnalyticsCard>
                  </Grid>
                )}
              </Grid>
            </AnalyticsSection>

            <AnalyticsSection
              title="Funding analytics"
              loading={detailsLoading}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Applications"
                    value={lshFormatInteger(applications.length)}
                    detail={`${averageApplicationCompletion}% avg complete`}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Total ask"
                    value={lshFormatCurrency(totalFundingAsk)}
                    detail="Selected period"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Funding categories"
                    value={lshFormatInteger(fundingCategoryRows.length)}
                    detail="Cost categories"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsBreakdownList
                    title="Application status"
                    rows={fundingStatusRows}
                    valueLabel="Ask"
                    valueFormatter={lshFormatCurrency}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsBreakdownList
                    title="Funding ask by category"
                    rows={fundingCategoryRows}
                    valueLabel="Ask"
                    valueFormatter={lshFormatCurrency}
                  />
                </Grid>
              </Grid>
            </AnalyticsSection>

            <AnalyticsSection
              title="Program analytics"
              loading={detailsLoading}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Programs"
                    value={lshFormatInteger(programs.length)}
                    detail="Selected period"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Hectares"
                    value={lshFormatInteger(totalProgramHectares)}
                    detail="Program hectares"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AnalyticsMetric
                    label="Products in programs"
                    value={lshFormatInteger(
                      programs.reduce(
                        (total, program) =>
                          total + Number(program.lines?.length || 0),
                        0,
                      ),
                    )}
                    detail="Program lines"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsBreakdownList
                    title="Program status"
                    rows={programStatusRows}
                    valueLabel="Hectares"
                    valueFormatter={lshFormatInteger}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsBreakdownList
                    title="Program type"
                    rows={programTypeRows}
                    valueLabel="Hectares"
                    valueFormatter={lshFormatInteger}
                  />
                </Grid>
              </Grid>
            </AnalyticsSection>

            <AnalyticsSection
              title="Product activity from quotes and orders"
              loading={detailsLoading}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <AnalyticsTopProductsList
                    title="Top quoted products"
                    rows={topQuoteProducts}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <AnalyticsTopProductsList
                    title="Top ordered products"
                    rows={topOrderProducts}
                  />
                </Grid>
              </Grid>
            </AnalyticsSection>
          </Paper>
        </Box>
      )}

      <AnalyticsReportDrawer
        open={reportDrawerOpen}
        onClose={() => setReportDrawerOpen(false)}
        dateRangeId={dateRangeId}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={setDateRangeId}
        onStartDateChange={handleSetStartDate}
        onEndDateChange={handleSetEndDate}
        reportSectionOptions={reportSectionOptions}
        selectedReportSections={selectedReportSections}
        onToggleSection={toggleReportSection}
        rangeLabel={reportRangeLabel}
        loading={detailsLoading}
        generating={reportGenerating}
        canGenerate={
          Boolean(stats && startDate && endDate) &&
          !detailsLoading &&
          !reportGenerating &&
          Boolean(selectedReportSections.length)
        }
        onGenerate={handleGenerateAnalyticsReport}
      />
    </div>
  );
}

const mapStateToProps = (state: {user: ISupplierUserProfile}) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(Analytics);
